import { Heart, Brain, Shield, Users, Calendar, MessageCircle, Sparkles, Clock } from 'lucide-react';
import TherapiesGrid from '../../components/TherapiesGrid';
import Meta from '../../components/Meta';
import { Link } from 'react-router-dom';

// Keywords and content for SEO optimization
const pageMetadata = {
  title: "Anger Management Counselling in Surrey, BC | Professional Support | Metro Counselling",
  description: "Expert anger management counselling in Surrey, BC. Professional support for emotional regulation, stress management, and better relationships. Book your consultation today.",
  keywords: "anger management Surrey, anger counselling BC, emotional regulation, stress management, anger management therapy Surrey",
};

export default function AngerManagement() {
  const techniques = [
    {
      icon: Brain,
      title: 'Cognitive Restructuring',
      description: 'Learn to identify and change thought patterns that trigger anger.',
    },
    {
      icon: Heart,
      title: 'Emotional Regulation',
      description: 'Develop skills to manage intense emotions effectively.',
    },
    {
      icon: Shield,
      title: 'Stress Management',
      description: 'Build resilience and coping strategies for stress reduction.',
    },
    {
      icon: Users,
      title: 'Communication Skills',
      description: 'Improve interpersonal relationships through better communication.',
    },
  ];

  const journeySteps = [
    {
      icon: MessageCircle,
      title: 'Initial Assessment',
      description: 'Understanding your unique triggers and anger patterns.',
    },
    {
      icon: Brain,
      title: 'Skill Building',
      description: 'Learning effective techniques for emotional regulation.',
    },
    {
      icon: Sparkles,
      title: 'Practice & Growth',
      description: 'Implementing strategies in real-life situations.',
    },
    {
      icon: Clock,
      title: 'Ongoing Support',
      description: 'Regular check-ins and adjustments to maintain progress.',
    },
  ];

  return (
    <>
      <Meta 
        title={pageMetadata.title}
        description={pageMetadata.description}
        keywords={pageMetadata.keywords}
      />
      
      <main className="pt-16" role="main">
        {/* Hero Section */}
        <section 
          className="relative min-h-[500px] md:h-[600px] mb-8 md:mb-12" 
          aria-label="Hero section"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('/images/optimized/anger_management_1.webp')",
            }}
            role="img"
            aria-label="Representative image for anger management counselling"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/40 to-gray-900/80"></div>
          </div>
          
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center items-center text-center py-12 md:py-0">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 md:mb-8 leading-tight">
              Transform Your Relationship<br className="hidden sm:block" />with Anger
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-3xl mb-6 md:mb-8 px-4">
              Professional anger management counselling in Surrey, BC
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-center w-full sm:w-auto">
              <a 
                href="tel:604-619-9992" 
                className="w-full sm:w-auto text-xl md:text-2xl text-white bg-brand-teal hover:bg-brand-teal-dark px-6 md:px-8 py-3 md:py-4 rounded-lg transition-all transform hover:scale-105"
                aria-label="Call us at 604-619-9992"
              >
                Call 604-619-9992
              </a>
              <a 
                href="#learn-more" 
                className="w-full sm:w-auto text-lg md:text-xl text-white border-2 border-white hover:bg-white hover:text-gray-900 px-6 md:px-8 py-3 md:py-4 rounded-lg transition-all"
                aria-label="Learn more about our anger management services"
              >
                Learn More
              </a>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <section id="learn-more" className="text-center mb-12 md:mb-16 scroll-mt-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              Professional Support for Anger Management
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Get the strength to handle your anger with our expert anger management counselling in Surrey, BC.
            </p>
          </section>

          <section className="bg-white rounded-xl shadow-sm p-6 md:p-8 mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Metro Counselling: Professional Support for Anger Management
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Feeling overwhelmed by your anger? Metro Counselling is here to help. Our team of expert counsellors offers a nonjudgmental environment to help you manage your emotions and find peace, leading to a more fulfilling life.
            </p>
          </section>

          <section className="bg-white rounded-xl shadow-sm p-6 md:p-8 mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Comprehensive Anger Management Solutions
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              While anger is a natural emotion, uncontrolled anger can be detrimental to relationships, career prospects, and health. Metro Counselling offers proven anger management techniques to help you regain control. We utilize evidence-based approaches such as cognitive-behavioral therapy (CBT), mindfulness techniques, and stress-relieving exercises to identify triggers and develop healthy coping mechanisms.
            </p>
          </section>

          <section 
            className="grid md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16"
            aria-label="Management techniques"
          >
            {techniques.map((technique, index) => (
              <article 
                key={index} 
                className="bg-white rounded-xl shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow border border-gray-100"
              >
                <technique.icon className="h-8 w-8 md:h-10 md:w-10 text-brand-teal mb-4 md:mb-6" />
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2 md:mb-3">
                  {technique.title}
                </h3>
                <p className="text-base md:text-lg text-gray-600">{technique.description}</p>
              </article>
            ))}
          </section>

          <section className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-sm p-8 md:p-12 mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
              Your Journey to Better Emotional Control
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {journeySteps.map((step, index) => (
                <article key={index} className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-brand-teal/10 flex items-center justify-center mb-4">
                    <step.icon className="h-7 w-7 md:h-8 md:w-8 text-brand-teal" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600">{step.description}</p>
                </article>
              ))}
            </div>
          </section>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-12 md:mb-16">
            <section className="bg-white rounded-xl shadow-sm p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4 md:mb-6">
                Is Anger Management Counselling Right for You?
              </h2>
              <div className="space-y-3 text-gray-600">
                <p>Individuals who benefit from anger management counselling include:</p>
                {[
                  'Excessive or frequent anger outbursts overwhelm you.',
                  'They don\'t know how to express themselves constructively.',
                  'Affects relationships because of unresolved anger.',
                  'You want to become more emotionally aware and resilient.',
                ].map((item, index) => (
                  <p key={index} className="flex items-center">
                    <span className="w-2 h-2 bg-brand-teal rounded-full mr-2"></span>
                    {item}
                  </p>
                ))}
              </div>
            </section>

            <section className="bg-white rounded-xl shadow-sm p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4 md:mb-6">
                Our Non-Judgmental Approach to Anger Management Counselling
              </h2>
              <div className="space-y-3 text-gray-600">
                <div>
                  <p className="font-semibold mb-1">Understanding Your Triggers</p>
                  <p>Our counsellors will work closely with you to address the underlying causes of your anger.</p>
                </div>
                <div>
                  <p className="font-semibold mb-1">Adaptation Strategies</p>
                  <p>Our therapists will help you identify and develop effective coping mechanisms for managing anger.</p>
                </div>
                <div>
                  <p className="font-semibold mb-1">Building Communication Skills</p>
                  <p>We will help you develop effective communication strategies that avoid triggering aggression.</p>
                </div>
                <div>
                  <p className="font-semibold mb-1">Progress Monitoring</p>
                  <p>Our goal is to ensure tangible results through consistent feedback and guided reflection throughout the counselling process.</p>
                </div>
              </div>
            </section>
          </div>

          <section className="bg-white rounded-xl shadow-sm p-6 md:p-8 mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4 md:mb-6">
              Possible Signs of Severe Anger That Require Professional Support
            </h2>
            <div className="space-y-3 text-gray-600">
              {[
                'Regular emotional or anger explosions',
                'Feeling physically agitated',
                'Struggling with relationships due to anger',
                'Regret after saying or doing things in anger',
                'Facing work or legal issues because of anger',
                'Experiencing physical symptoms during anger episodes'
              ].map((item, index) => (
                <p key={index} className="flex items-center">
                  <span className="w-2 h-2 bg-brand-teal rounded-full mr-2"></span>
                  {item}
                </p>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-xl shadow-sm p-6 md:p-8 mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4 md:mb-6">
              Benefits of Choosing Metro Counselling for Anger Management
            </h2>
            <ul className="space-y-2">
              {[
                'Improved relationships with family, friends, and colleagues',
                'Enhanced emotional intelligence and self-awareness',
                'Better decision-making under high-stress conditions',
                'Increased inner peace and reduced stress levels',
                'More positive and healthier outlook on life'
              ].map((item, index) => (
                <li key={index} className="flex items-center">
                  <span className="w-2 h-2 bg-brand-teal rounded-full mr-2"></span>
                  <p>{item}</p>
                </li>
              ))}
            </ul>
          </section>

          <section className="bg-white rounded-xl shadow-sm p-6 md:p-8 mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4 md:mb-6">
              Our Comprehensive Anger Management Services
            </h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                Our anger management counselling services aim to help you identify your triggers, improve your responses, and gain more control over your emotions.
              </p>
              <ul className="space-y-3">
                <li>
                  <Link to="/individual-counselling-therapy-surrey" className="text-brand-teal hover:underline font-semibold block mb-1">
                    Individual Counselling Sessions
                  </Link>
                  <p className="text-gray-600">We provide confidential one-on-one counselling sessions to examine the root causes of anger.</p>
                </li>
                <li>
                  <span
                    onClick={() => alert("Cognitive Behavioral Therapy (CBT) is a type of psychotherapy that helps people identify and change negative thinking patterns and behaviors. It is often used to treat anxiety, depression, and other mental health conditions.")}
                    className="text-brand-teal hover:underline cursor-pointer font-semibold block mb-1"
                  >
                    Cognitive Behavioral Therapy (CBT)
                  </span>
                  <p className="text-gray-600">We help you replace negative thought patterns with positive, constructive ones using proven CBT techniques.</p>
                </li>
                <li>
                  <Link to="/tele-health-system" className="text-brand-teal hover:underline font-semibold block mb-1">
                    Stress Management
                  </Link>
                  <p className="text-gray-600">Our sessions integrate mindfulness, deep breathing, and relaxation methods to relieve anger-causing tension.</p>
                </li>
                <li>
                  <p className="font-semibold mb-1">Anger Awareness Training</p>
                  <p className="text-gray-600">We help you identify and anticipate situations that trigger anger through targeted exercises.</p>
                </li>
                <li>
                  <p className="font-semibold mb-1">Conflict Resolution</p>
                  <p className="text-gray-600">Learn effective strategies to diffuse tense situations and foster better relationships.</p>
                </li>
                <li>
                  <p className="font-semibold mb-1">Emotional Resilience Building</p>
                  <p className="text-gray-600">Our counsellors provide lifelong emotional resilience training to help prevent and manage anger episodes.</p>
                </li>
              </ul>
            </div>
          </section>

          <section className="bg-gradient-to-br from-brand-teal/10 to-brand-teal/5 rounded-xl p-8 md:p-12 mb-12 md:mb-16">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
                Get started today to shape your Anger into calmness. 
              </h2>
              <p className="text-lg md:text-xl text-gray-600 mb-6 md:mb-8">
                Live your life without letting anger define you. With our expert anger management counsellors on hand, you can take charge of your emotions today. We are here to help you become a more calm, happy, and fulfilled version of yourself. 
              </p>
              <div className="flex flex-col items-center">
                <p className="text-lg md:text-xl font-medium text-gray-900 mb-4">
                  Schedule Your Consultation
                </p>
                <a 
                  href="tel:604-619-9992" 
                  className="inline-flex items-center gap-2 bg-brand-teal hover:bg-brand-teal-dark text-white font-medium px-6 md:px-8 py-3 md:py-4 rounded-lg transition-all transform hover:scale-105 w-full sm:w-auto justify-center"
                  aria-label="Call 604-619-9992 to schedule your consultation"
                >
                  <Calendar className="h-5 w-5 md:h-6 md:w-6" />
                  <span className="text-lg md:text-xl">604-619-9992</span>
                </a>
              </div>
            </div>
          </section>

          <section aria-label="Additional therapy services">
            <TherapiesGrid />
          </section>
        </div>
      </main>
    </>
  );
}
